// MOBIL-KÁRTYA label:érték lista — a kibontott „Részletek" blokk EGYETLEN tipográfiája.
//
// MIÉRT KÖZÖS KOMPONENS: a kinyitott kártyán KÉT forrásból jön tartalom — (1) a cím-oszlop saját részletei
// (`cardExtra`, pl. az eszköz azonosító-mezői) és (2) a többi oszlop `label: érték` listája (`DataTable`). A
// kettő EGY blokként olvasódik, tehát ha külön-külön stílusuk van, a kártya „két különböző táblázat egymás
// alatt" hatást ad (mérve: az egyik `text-xs` + bal oldali vonal, a másik `text-sm` rács — a user épp ezt
// jelezte). Egy komponensben tartva nem tudnak elcsúszni egymástól.
//
// A DESKTOP-nézet NEM ezt használja: ott a részletek a cella SAJÁT kibontójában élnek, ahol a kompaktabb,
// vonallal jelölt alak a helyes (a sor-magasság kötött, és a blokk egy cellán belül van) — ez tudatos
// különbség, nem elcsúszás.
import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * A KÁRTYA RÉSZLETEK-BLOKKJÁNAK MÉRET-ZÁRA: ami idebent 13 px-re van állítva, az 12-re csökken.
 *
 * MÉRT HIBA (ez a konstans ezért létezik, user-lelet): a részletek-blokk 12 px-es (`text-sm`), és a benne
 * álló cella-tartalom ezt ÖRÖKLI — kivéve, ami SAJÁT méretet hoz. A `<Text>` alap-variánsa (`body`) pedig
 * 13 px: egy `<Text tone="tertiary">` másodlagos sor tehát NAGYOBB lett, mint a fölötte álló, elsődleges
 * sor. Pontosan fordítva, mint a szándék. Deszktopon nincs hiba, mert ott a tábla törzse is 13 px — a
 * bajt kizárólag a kártya kisebb skálája hozza elő, és ott MINDEN érintett lista-oldalon egyszerre.
 *
 * MIÉRT ÍGY, ÉS NEM CELLÁNKÉNT: a cellák a DESKTOP-nézetben helyesek, és ugyanaz a JSX szolgálja ki a két
 * nézetet — ha a cellában írnánk át a méretet, a deszktop romlana el. A skála tehát oda tartozik, ahol a
 * KONTEXTUS változik: a kártya-blokkra. Egy helyen, minden lista-oldalra.
 *
 * A szelektor specificitása (0,2,0) nagyobb, mint a puszta `.text-md`-é (0,1,0), tehát a sorrendtől
 * függetlenül nyer; a `text-sm` a sor-magasságot is viszi (nem csak a betűméretet).
 */
export const CARD_DETAIL_SCALE = '[&_.text-md]:text-sm';

export interface CardDetailItem {
  /**
   * A mező neve (a `dt`) — a kártyán kettőspont NÉLKÜL (a rács adja a tagolást). SZÁNDÉKOSAN `ReactNode`:
   * a `DataTable` az oszlop `header`-ét adja át, ami lehet ikon vagy összetett elem is — stringgé kényszerítve
   * `[object Object]` lenne belőle. Ilyenkor a `key` adja a stabil azonosítót.
   */
  label: ReactNode;
  /** Az érték: string vagy komplex cella (a `DataTable` a teljes cella-renderelést átadja). */
  value: ReactNode;
  /** Stabil kulcs, ha a `label` nem egyedi (a `DataTable` az oszlop-id-t adja). */
  key?: string;
}

export interface CardDetailListProps {
  items: CardDetailItem[];
  /** A blokk-szintű térköz/határ (a hívó dönti el, kell-e felső hairline — az ELSŐ blokk hozza). */
  className?: string;
}

/**
 * A `label: érték` párok kétkolumnás rácsa. A címke-kolumna a tartalomhoz szűkül (`auto`), az érték kapja a
 * maradékot (`1fr`) — így a kártya minden sora egy vonalban indul, függetlenül a címke hosszától.
 */
export function CardDetailList({ items, className }: CardDetailListProps): JSX.Element {
  return (
    <dl
      className={cn(
        'grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm',
        CARD_DETAIL_SCALE,
        className,
      )}
    >
      {items.map((it, i) => (
        // A kulcs: az explicit `key`, egyébként a string-címke, végső esetben az index (a `label` ReactNode is
        // lehet — abból nem képezhető React-kulcs).
        <div key={it.key ?? (typeof it.label === 'string' ? it.label : i)} className="contents">
          <dt className="text-muted-foreground whitespace-nowrap">{it.label}</dt>
          {/* Balra igazított érték: a skalár mezők és a komplex cellák (pl. ExpandableCell) egységesen olvashatók. */}
          <dd className="text-foreground min-w-0">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}
